import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Link,
  SettingsInputAntenna,
  List as ListIcon,
  Send as SendIcon,
  CheckCircle,
  Cancel,
  Map,
} from "@mui/icons-material";
import { JSONTree } from "react-json-tree";
import { fetchSecure } from "../utils/utils";
import SuperAppButton from "../components/SuperAppButton";

export default function MCPClientUI() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const toast = {
    success: (msg) => {
      setMessage(msg);
      setOpen(true);
    },
    info: (msg) => {
      setMessage(msg);
      setOpen(true);
    },
    warn: (msg) => {
      setMessage(msg);
      setOpen(true);
    },
    error: (msg) => {
      setMessage(msg);
      setOpen(true);
    },
  };

  const mcpsServers = [
    {
      id: "google_services",
      name: "Google Services",
      url: "/api/google-service",
      description:
        "Provides Google Maps, Places, Distance Matrix, and other Google APIs",
      placeholder: "Plan a trip from Boston to Providence?",
      icon: <Map />,
    },
    {
      id: "youtube_services",
      name: "YouTube Services",
      url: "/api/youtube-service",
      description:
        "Provides YouTube search, video details, and channel insights",
      placeholder: "Videos on Evolution of Artificial Intelligence?",
      icon: <SettingsInputAntenna />,
    },
  ];

  const [selectedServer, setSelectedServer] = useState(mcpsServers[0]);
  const [clientUrl, setClientUrl] = useState("/api/google-service");
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState({ connected: false, tools: [] });
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setStatus({ connected: false, tools: [] });
  }, [selectedServer]);

  const connectToServer = async () => {
    try {
      setLoading(true);
      const res = await fetchSecure(`${clientUrl}/connect`, {
        headers: { "Content-Type": "application/json" },
        method: "post",
        body: JSON.stringify({ mcp_server_type: selectedServer.id }),
      });
      const data = await res.json();

      setStatus({
        connected: data.connected,
        tools: data.tools || [],
      });
      toast.success(
        data.connected
          ? `Connected to ${selectedServer.name}!`
          : "Connection failed"
      );
    } catch (error) {
      toast.error(`Failed to connect to ${selectedServer.name}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    try {
      setLoading(true);
      const res = await fetchSecure(`${clientUrl}/status`);
      const data = await res.json();
      setStatus(data);
      toast.info(`${selectedServer.name} status checked`);
    } catch (error) {
      toast.error("Failed to check status");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTools = async () => {
    try {
      setLoading(true);
      const res = await fetchSecure(`${clientUrl}/tools`);
      const data = await res.json();
      setStatus((prev) => ({ ...prev, tools: data }));
      toast.success(`${selectedServer.name} tools fetched`);
    } catch (error) {
      toast.error("Failed to fetch tools");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const submitQuery = async () => {
    if (!query.trim()) {
      toast.warning("Please enter a query");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        query,
      };

      const res = await fetchSecure(`${clientUrl}/query`, {
        headers: { "Content-Type": "application/json" },
        method: "post",
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      setResponse(data.response);
      setHistory((prev) => [
        ...prev,
        {
          query,
          response: data.response,
          server: selectedServer.name,
        },
      ]);
      toast.success("Query processed");
      setQuery("");
    } catch (error) {
      toast.error("Failed to process query");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      /* Vik(Jun 20, 2025): Changed styling to make it more responsive and visually appealing */
      sx={{
        width: "100%",
        maxWidth: "100%",
        margin: "auto",
        padding: isMobile ? 2 : 4,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        color: "#fffffc",
        p:1
      }}
    >
      <Grid container spacing={3} sx={{fontFamily:"inherit"}}>
        <Grid item xs={12} md={6}>
          <div
     
            style={{
              p: 3,
              backgroundColor: "transparent",
              color: "#fffffc",
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{marginBlock:"5px"}} >
              Server Configuration
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: "#94a6db" }}>MCP Server</InputLabel>
              <Select
                value={selectedServer.id}
                onChange={(e) => {
                  const server = mcpsServers.find(
                    (s) => s.id === e.target.value
                  );
                  if (server) {
                    setSelectedServer(server);
                    setClientUrl(server.url);
                  }
                }}
                label="MCP Server"
                sx={{
                  color: "#fffffc",
                  ".MuiSelect-icon": { color: "#fffffc" },
                  mb: 2,
                }}
              >
                {mcpsServers.map((server) => (
                  <MenuItem key={server.id} value={server.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {server.icon}
                      {server.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <SuperAppButton
                variant="contained"
                startIcon={<Link />}
                onClick={connectToServer}
                disabled={loading}
                sx={{
                  backgroundColor: "#9355eb",
                  "&:hover": { backgroundColor: "#7d3fc9" },
                }}
              >
                Connect
              </SuperAppButton>
              <SuperAppButton
                variant="outlined"
                startIcon={<SettingsInputAntenna />}
                onClick={checkStatus}
                disabled={loading}
                sx={{
                  borderColor: "#6932fe",
                  color: "#6932fe",
                  "&:hover": { borderColor: "#5a2bd4" },
                }}
              >
                Check Status
              </SuperAppButton>
            </Box>

            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography>Status:</Typography>
              {status.connected ? (
                <Chip
                  icon={<CheckCircle />}
                  label="Connected"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<Cancel />}
                  label="Disconnected"
                  color="error"
                  size="small"
                />
              )}
              <Typography sx={{ color: "#94a6db" }}>
                {status.tools.length > 0
                  ? `${status.tools.length} tools available`
                  : "No tools"}
              </Typography>
            </Box>

            {status.tools.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" >
                  Available Tools ({selectedServer.name})
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {status.tools.map((tool, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={tool.name}
                        secondary={tool.description}
                        primaryTypographyProps={{ color: "#fffffc" }}
                        secondaryTypographyProps={{ color: "#94a6db" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div
         
            style={{
              
              backgroundColor: "#transparent",
              color: "#fffffc",
              height: "100%",
            }}
          >
            <Typography variant="h6" >
              Query Interface
            </Typography>
            <Notify
              open={open}
              setOpen={setOpen}
              message={message}
              setMessage={setMessage}
            ></Notify>
            <TextField
              label={`Ask ${selectedServer.name}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
              multiline
              rows={4}
              disabled={loading || !status.connected}
              placeholder={`e.g. ${selectedServer.placeholder}`}
              sx={{
                mb: 2,
                input: { color: "#fffffc" },
                textarea: { color: "#fffffc" },
                label: { color: "#94a6db" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#6932fe",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9355eb",
                  },
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SuperAppButton
                variant="contained"
                startIcon={<SendIcon />}
                onClick={submitQuery}
                disabled={loading || !status.connected}
                sx={{
                  backgroundColor: "#9355eb",
                  "&:hover": { backgroundColor: "#7d3fc9" },
                  flexShrink: 0,
                }}
              >
                Submit Query
              </SuperAppButton>
              {loading && (
                <CircularProgress size={24} sx={{ color: "#9355eb" }} />
              )}
            </Box>

            {response && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  mt: 3,
                  backgroundColor: "#232c7a",
                  color: "#fffffc",
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                <ResponseGuide />
                <Box sx={{ minWidth: 0 }}>
                  <JSONTree
                    data={
                      typeof response === "string" ? { response } : response
                    }
                    hideRoot={true}
                    shouldExpandNode={(keyPath, data, level) => level < 1}
                    theme={{
                      scheme: "twilight",
                      base00: "#232c7a",
                      base01: "#383838",
                      base02: "#494949",
                      base03: "#5f5a60",
                      base04: "#838184",
                      base05: "#a7a7a7",
                      base06: "#c3c3c3",
                      base07: "#ffffff",
                      base08: "#cf6a4c",
                      base09: "#cda869",
                      base0A: "#f9ee98",
                      base0B: "#8f9d6a",
                      base0C: "#afc4db",
                      base0D: "#7587a6",
                      base0E: "#9b859d",
                      base0F: "#9b703f",
                    }}
                    invertTheme={false}
                    labelRenderer={([key]) => <span>{key}</span>}
                    valueRenderer={(raw) => (
                      <span style={{ fontSize: "0.9rem" }}>{raw}</span>
                    )}
                  />
                </Box>
              </Paper>
            )}
          </div>
        </Grid>
      </Grid>

      {history.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: "#1f266e",
            color: "#fffffc",
            mt: 2,
          }}
        >
          <Typography variant="h6" >
            Query History
          </Typography>
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {history.map((item, index) => (
              <Box key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#94a6db", mb: 1 }}
                  >
                    Query to {item.server}:
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      mb: 2,
                      p: 1,
                      backgroundColor: "#232c7a",
                      borderRadius: 1,
                    }}
                  >
                    <JSONTree
                      data={
                        typeof item.query === "string"
                          ? { query: item.query }
                          : item.query
                      }
                      hideRoot={true}
                      shouldExpandNode={(keyPath, data, level) => level < 1}
                      theme={{
                        scheme: "twilight",
                        base00: "#232c7a",
                        base01: "#383838",
                        base02: "#494949",
                        base03: "#5f5a60",
                        base04: "#838184",
                        base05: "#a7a7a7",
                        base06: "#c3c3c3",
                        base07: "#ffffff",
                        base08: "#cf6a4c",
                        base09: "#cda869",
                        base0A: "#f9ee98",
                        base0B: "#8f9d6a",
                        base0C: "#afc4db",
                        base0D: "#7587a6",
                        base0E: "#9b859d",
                        base0F: "#9b703f",
                      }}
                      invertTheme={false}
                      labelRenderer={([key]) => <span>{key}</span>}
                      valueRenderer={(raw) => (
                        <span style={{ fontSize: "0.8rem" }}>{raw}</span>
                      )}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#94a6db", mb: 1 }}
                  >
                    Response:
                  </Typography>
                  <Box
                    sx={{
                      minWidth: 0,
                      p: 1,
                      backgroundColor: "#232c7a",
                      borderRadius: 1,
                    }}
                  >
                    <JSONTree
                      data={item.response}
                      hideRoot={true}
                      shouldExpandNode={(keyPath, data, level) => level < 1} // Collapse beyond level 1
                      theme={{
                        scheme: "twilight",
                        base00: "#232c7a", // Background color
                        base01: "#383838",
                        base02: "#494949",
                        base03: "#5f5a60",
                        base04: "#838184",
                        base05: "#a7a7a7",
                        base06: "#c3c3c3",
                        base07: "#ffffff",
                        base08: "#cf6a4c",
                        base09: "#cda869",
                        base0A: "#f9ee98",
                        base0B: "#8f9d6a",
                        base0C: "#afc4db",
                        base0D: "#7587a6",
                        base0E: "#9b859d",
                        base0F: "#9b703f",
                      }}
                      invertTheme={false}
                      labelRenderer={([key]) => <span>{key}</span>}
                      valueRenderer={(raw) => (
                        <span style={{ fontSize: "0.8rem" }}>{raw}</span>
                      )}
                    />
                  </Box>
                </ListItem>
                {index < history.length - 1 && (
                  <Divider
                    sx={{
                      borderColor: "#6932fe",
                      my: 2,
                      mx: "auto",
                      width: "90%",
                    }}
                  />
                )}
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

function Notify({ open, setOpen, message, setMessage }) {
  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    else {
      setOpen(false);
      setMessage("");
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          {message || ""}
        </Alert>
      </Snackbar>
    </div>
  );
}

function ResponseGuide() {
  return (
    <Tooltip title="Expand it to see data" arrow>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Response:
      </Typography>
    </Tooltip>
  );
}
